import { useState, memo } from 'react';
import { ChevronDown, ChevronUp, FileText, BookOpen, Scale, FileCheck, CheckCircle2, FileEdit } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useSessionStore } from '@/stores';
import { useNotificationStore } from '@/stores';
import { draftApi } from '@/services/api';

interface MessageContextResource {
  resource_type: string;
  resource_id: string;
  resource_name?: string;
  use_summary?: boolean;
  use_biblio_info?: boolean;
  use_abstract?: boolean;
  use_entire_description?: boolean;
  use_claims?: boolean;
  paragraphs?: number[];
  claims?: number[];
}

interface VDBResource {
  mpep_sections?: string[];
  cfr_sections?: string[];
  usc_sections?: string[];
  cfr_info?: Array<{
    section: string;
    title: string;
    content?: string;
  }>;
  fed_reg_docs?: Array<{
    document_number: string;
    title: string;
  }>;
  classification_codes?: string[];
}

interface MessageContextProps {
  messageContext?: {
    resources?: MessageContextResource[];
    vdb_discovered_resources?: VDBResource;
    search_terms?: string[];
    mpep_sections?: string[];
    cfr_sections?: string[];
    usc_sections?: string[];
  };
  sessionContext?: any;
  onDocumentClick?: (doc: any) => void;
  compactMode?: boolean;
}

const MessageContextComponent = ({ messageContext, sessionContext, onDocumentClick, compactMode = false }: MessageContextProps) => {
  const [expanded, setExpanded] = useState(false);
  const { addNotification } = useNotificationStore();

  if (!messageContext) {
    return null;
  }

  // Deduplicate resources, especially drafts (which might appear twice - once with name, once with just ID)
  const rawResources = messageContext.resources || [];
  const resourceMap = new Map<string, MessageContextResource>();

  rawResources.forEach(resource => {
    // Create a unique key based on type and ID
    const key = `${resource.resource_type || 'unknown'}_${resource.resource_id}`;

    // If this resource already exists, only replace if the new one has a better name
    const existing = resourceMap.get(key);
    if (existing) {
      // Prefer resources with resource_name over those without
      if (resource.resource_name && !existing.resource_name) {
        resourceMap.set(key, resource);
      }
      // Skip if existing is better or equal
    } else {
      resourceMap.set(key, resource);
    }
  });

  const resources = Array.from(resourceMap.values());
  const vdbResources = messageContext.vdb_discovered_resources || {};
  const searchTerms = messageContext.search_terms || [];

  // Merge top-level sections with vdb_discovered_resources sections
  const mpepSections = [
    ...(messageContext.mpep_sections || []),
    ...(vdbResources.mpep_sections || [])
  ].filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates

  const cfrSections = [
    ...(messageContext.cfr_sections || []),
    ...(vdbResources.cfr_sections || [])
  ].filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates

  const uscSections = [
    ...(messageContext.usc_sections || []),
    ...(vdbResources.usc_sections || [])
  ].filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates

  // Check if there's any content to display
  const hasResources = resources.length > 0;
  const hasVdbResources = mpepSections.length > 0 || cfrSections.length > 0 ||
                          uscSections.length > 0 || vdbResources.classification_codes?.length ||
                          vdbResources.fed_reg_docs?.length;
  const hasSearchTerms = searchTerms.length > 0;
  const hasAnyContent = hasResources || hasVdbResources || hasSearchTerms;

  // If no content at all, show "no documents used" message
  if (!hasAnyContent) {
    return (
      <div className="rounded-lg">
        <div className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-gray-600 italic">
            No specific documents were used in generating this response
          </span>
        </div>
      </div>
    );
  }

  // Get icon for resource type
  const getResourceIcon = (type: string | undefined, resourceName?: string) => {
    // Check if it's a draft by name pattern
    if (resourceName && (resourceName.includes('Active Drafts:') || resourceName.includes('Draft Content'))) {
      return FileEdit;
    }

    if (!type) return FileText;

    switch (type) {
      case 'patent':
      case 'publication':
      case 'application':
        return FileText;
      case 'upload':
        return FileCheck;
      case 'draft':
        return FileEdit;
      default:
        return FileText;
    }
  };

  // Format resource display name
  const getResourceDisplayName = (resource: MessageContextResource) => {
    if (resource.resource_name) {
      // Clean up draft name by removing "Active Drafts:" prefix
      if (resource.resource_name.includes('Active Drafts:')) {
        return resource.resource_name.replace(/^Active Drafts:\s*/, '').trim();
      }
      return resource.resource_name;
    }
    if (!resource.resource_type) {
      return resource.resource_id || 'Unknown Resource';
    }
    const type = resource.resource_type.charAt(0).toUpperCase() + resource.resource_type.slice(1);
    return `${type} ${resource.resource_id}`;
  };

  // Check if resource is already in context
  const isResourceInContext = (resource: MessageContextResource) => {
    if (!resource.resource_type) return false;

    const storedContext = localStorage.getItem('session_context');
    if (!storedContext || storedContext === 'undefined') return false;

    try {
      const context = JSON.parse(storedContext);

      // Check based on resource type
      if (resource.resource_type === 'upload') {
        const resourceNames = context.resource_names || [];
        return resourceNames.includes(resource.resource_name || resource.resource_id);
      } else if (resource.resource_type === 'patent') {
        const patents = context.current_patents || [];
        return patents.includes(resource.resource_id);
      } else if (resource.resource_type === 'publication') {
        const publications = context.current_publications || [];
        return publications.includes(resource.resource_id);
      } else if (resource.resource_type === 'application') {
        const applications = context.current_applications || [];
        return applications.includes(resource.resource_id);
      } else if (resource.resource_type === 'draft') {
        // Check active drafts from localStorage
        const activeDraftsStr = localStorage.getItem('active_drafts');
        const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];
        return activeDrafts.some((draft: any) => draft.draft_id === resource.resource_id);
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  // Add resource to context
  const handleAddToContext = async (resource: MessageContextResource) => {
    if (!resource.resource_type) {
      addNotification({
        type: 'error',
        title: 'Invalid Resource',
        message: 'Resource type is missing',
      });
      return;
    }

    try {
      const storedContext = localStorage.getItem('session_context');
      const context = storedContext && storedContext !== 'undefined' ? JSON.parse(storedContext) : {};

      if (resource.resource_type === 'patent' || resource.resource_type === 'publication' || resource.resource_type === 'application') {
        const resourceIds = context.resource_ids || [];
        const patents = context.current_patents || [];
        const publications = context.current_publications || [];
        const applications = context.current_applications || [];

        // Check for duplicates in the specific array
        let isDuplicate = false;
        if (resource.resource_type === 'patent') {
          isDuplicate = patents.includes(resource.resource_id);
        } else if (resource.resource_type === 'publication') {
          isDuplicate = publications.includes(resource.resource_id);
        } else if (resource.resource_type === 'application') {
          isDuplicate = applications.includes(resource.resource_id);
        }

        if (isDuplicate) {
          addNotification({
            type: 'info',
            title: 'Already in Session',
            message: `${getResourceDisplayName(resource)} is already in your session.`,
          });
          return;
        }

        // Add to context
        resourceIds.push(resource.resource_id);
        if (resource.resource_type === 'patent') {
          patents.push(resource.resource_id);
        } else if (resource.resource_type === 'publication') {
          publications.push(resource.resource_id);
        } else if (resource.resource_type === 'application') {
          applications.push(resource.resource_id);
        }

        context.resource_ids = resourceIds;
        context.current_patents = patents;
        context.current_publications = publications;
        context.current_applications = applications;

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        addNotification({
          type: 'success',
          title: 'Added to Session',
          message: `${getResourceDisplayName(resource)} has been added to your session.`,
        });
      } else if (resource.resource_type === 'upload') {
        const resourceNames = context.resource_names || [];
        const resourceIds = context.resource_ids || [];

        if (resourceNames.includes(resource.resource_name || resource.resource_id)) {
          addNotification({
            type: 'info',
            title: 'Already in Session',
            message: `${resource.resource_name || resource.resource_id} is already in your session.`,
          });
          return;
        }

        resourceNames.push(resource.resource_name || resource.resource_id);
        resourceIds.push(resource.resource_id);

        context.resource_names = resourceNames;
        context.resource_ids = resourceIds;

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        addNotification({
          type: 'success',
          title: 'Added to Session',
          message: `${resource.resource_name || resource.resource_id} has been added to your session.`,
        });
      } else if (resource.resource_type === 'draft') {
        // Check if draft is already in active documents
        const activeDraftsStr = localStorage.getItem('active_drafts');
        const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];

        const isAlreadyActive = activeDrafts.some((d: any) => d.draft_id === resource.resource_id);
        if (isAlreadyActive) {
          addNotification({
            type: 'info',
            title: 'Already Active',
            message: `${getResourceDisplayName(resource)} is already in Active Documents.`,
          });
          return;
        }

        // Fetch full draft content and add to active drafts
        try {
          const fullDraft = await draftApi.getDraft(resource.resource_id);
          activeDrafts.push({
            draft_id: fullDraft.draft_id,
            title: fullDraft.title || resource.resource_name || 'Untitled Draft',
            content: fullDraft.content || ''
          });

          localStorage.setItem('active_drafts', JSON.stringify(activeDrafts));
          window.dispatchEvent(new Event('storage-update'));

          addNotification({
            type: 'success',
            title: 'Added to Active Documents',
            message: `${getResourceDisplayName(resource)} has been added to Active Documents.`,
          });
        } catch (error) {
          console.error('Error fetching draft:', error);
          addNotification({
            type: 'error',
            title: 'Failed to Add',
            message: 'Failed to fetch draft content. Please try again.',
          });
        }
      }
    } catch (error) {
      console.error('Error adding resource to context:', error);
      addNotification({
        type: 'error',
        title: 'Failed to Add',
        message: 'Failed to add resource to session. Please try again.',
      });
    }
  };

  // Render resource badges with details
  const ResourceBadge = ({ resource }: { resource: MessageContextResource }) => {
    const Icon = getResourceIcon(resource.resource_type, resource.resource_name);
    const inContext = isResourceInContext(resource);

    const handleResourceClick = () => {
      // Check if this is a draft - either by resource_type or by resource_name pattern
      const isDraft = (resource.resource_type && resource.resource_type.toLowerCase() === 'draft') ||
                      (resource.resource_name && (
                        resource.resource_name.includes('Active Drafts:') ||
                        resource.resource_name.includes('Draft Content')
                      ));

      // Handle draft clicks differently - open in split view
      if (isDraft) {
        localStorage.setItem('open_draft_id', resource.resource_id);
        window.dispatchEvent(new Event('open-draft'));
      } else if (onDocumentClick && resource.resource_type) {
        onDocumentClick({
          id: resource.resource_id,
          type: resource.resource_type.charAt(0).toUpperCase() + resource.resource_type.slice(1),
          title: getResourceDisplayName(resource),
          resource_id: resource.resource_id,
        });
      }
    };

    if (!expanded) {
      // Collapsed mode: compact inline display with click
      return (
        <span
          onClick={(e) => {
            e.stopPropagation();
            handleResourceClick();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '0.25rem',
            padding: '0.125rem 0.5rem',
            fontSize: '0.75rem',
            cursor: 'pointer',
            pointerEvents: 'auto'
          }}
          title={`Click to view ${getResourceDisplayName(resource)}`}
        >
          <Icon className="w-3 h-3 text-gray-600 flex-shrink-0 pointer-events-none" />
          <span className="font-medium text-gray-900 pointer-events-none">
            {getResourceDisplayName(resource)}
          </span>
          {inContext && (
            <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 pointer-events-none" />
          )}
        </span>
      );
    }

    // Expanded mode: full card with details and click
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          handleResourceClick();
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          padding: '0.5rem',
          cursor: 'pointer',
          pointerEvents: 'auto'
        }}
        title={`Click to view ${getResourceDisplayName(resource)}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0 pointer-events-none">
          <Icon className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-900 truncate">
            {getResourceDisplayName(resource)}
          </span>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0 pointer-events-none">
          {resource.paragraphs && resource.paragraphs.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              ¶{resource.paragraphs.join(', ')}
            </Badge>
          )}
          {resource.claims && resource.claims.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              Cl. {resource.claims.join(', ')}
            </Badge>
          )}
          {resource.use_summary && (
            <Badge variant="secondary" className="text-xs">Summary</Badge>
          )}
          {resource.use_abstract && (
            <Badge variant="secondary" className="text-xs">Abstract</Badge>
          )}
          {resource.use_claims && (
            <Badge variant="secondary" className="text-xs">Claims</Badge>
          )}
          {resource.use_entire_description && (
            <Badge variant="secondary" className="text-xs">Full Desc</Badge>
          )}
        </div>

        {inContext ? (
          <div className="flex items-center gap-1 text-xs text-green-600 flex-shrink-0">
            <CheckCircle2 className="w-4 h-4" />
            <span>In Session</span>
          </div>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              handleAddToContext(resource);
            }}
          >
            Add to Session
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
      <div className={`flex ${expanded ? 'items-start' : 'items-center'} justify-between gap-2`}>
        <div className="flex-1 min-w-0">
          {/* Resources - Single line when collapsed */}
          {resources.length > 0 && (
            <div className={expanded ? "mb-2" : ""}>
              {expanded ? (
                <>
                  <div className="text-xs font-medium text-blue-700 mb-2">References Used:</div>
                  <div className="flex flex-wrap gap-2">
                    {resources.map((resource, idx) => (
                      <ResourceBadge key={idx} resource={resource} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-1.5 flex-wrap" title="These resources were used in generating the message below.">
                  <span className="text-xs font-medium text-blue-700 whitespace-nowrap">References:</span>
                  {resources.slice(0, 4).map((resource, idx) => (
                    <ResourceBadge key={idx} resource={resource} />
                  ))}
                  {resources.length > 4 && (
                    <span className="text-xs text-gray-600">
                      +{resources.length - 4} more
                    </span>
                  )}

                  {/* VDB Resources inline on same line */}
                  {(mpepSections.length > 0 || cfrSections.length > 0 || uscSections.length > 0) && (
                    <>
              {mpepSections.length > 0 && (
                <>
                  <BookOpen className="w-3 h-3 text-blue-700 flex-shrink-0" />
                  <span className="text-xs font-medium text-blue-700 whitespace-nowrap">MPEP:</span>
                  {mpepSections.slice(0, 6).map((section, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center bg-white border border-gray-200 rounded px-2 py-0.5 text-xs cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      onClick={() => onDocumentClick?.({ id: section, type: 'Mpep', title: `MPEP ${section}`, resource_id: section })}
                      title={`Click to view MPEP ${section}`}
                    >
                      {section}
                    </span>
                  ))}
                  {mpepSections.length > 6 && (
                    <span className="text-xs text-gray-600">
                      +{mpepSections.length - 6} more
                    </span>
                  )}
                </>
              )}

              {cfrSections.length > 0 && (
                <>
                  <Scale className="w-3 h-3 text-blue-700 flex-shrink-0" />
                  <span className="text-xs font-medium text-blue-700 whitespace-nowrap">CFR:</span>
                  {cfrSections.slice(0, 6).map((section, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center bg-white border border-gray-200 rounded px-2 py-0.5 text-xs cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      onClick={() => onDocumentClick?.({ id: section, type: 'Cfr', title: `37 CFR ${section}`, resource_id: section })}
                      title={`Click to view 37 CFR ${section}`}
                    >
                      {section}
                    </span>
                  ))}
                  {cfrSections.length > 6 && (
                    <span className="text-xs text-gray-600">
                      +{cfrSections.length - 6} more
                    </span>
                  )}
                </>
              )}

              {uscSections.length > 0 && (
                <>
                  <Scale className="w-3 h-3 text-blue-700 flex-shrink-0" />
                  <span className="text-xs font-medium text-blue-700 whitespace-nowrap">USC:</span>
                  {uscSections.slice(0, 6).map((section, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center bg-white border border-gray-200 rounded px-2 py-0.5 text-xs cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      onClick={() => onDocumentClick?.({ id: section, type: 'Usc', title: `35 USC ${section}`, resource_id: section })}
                      title={`Click to view 35 USC ${section}`}
                    >
                      {section}
                    </span>
                  ))}
                  {uscSections.length > 6 && (
                    <span className="text-xs text-gray-600">
                      +{uscSections.length - 6} more
                    </span>
                  )}
                </>
              )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* VDB Resources - Only show when expanded */}
          {expanded && (
            <>
              {mpepSections.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                    <BookOpen className="w-3 h-3" />
                    MPEP Sections:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {mpepSections.map((section, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs bg-white cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        onClick={() => onDocumentClick?.({ id: section, type: 'Mpep', title: `MPEP ${section}`, resource_id: section })}
                        title={`Click to view MPEP ${section}`}
                      >
                        {section}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {cfrSections.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                    <Scale className="w-3 h-3" />
                    CFR Sections:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {cfrSections.map((section, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs bg-white cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        onClick={() => onDocumentClick?.({ id: section, type: 'Cfr', title: `37 CFR ${section}`, resource_id: section })}
                        title={`Click to view 37 CFR ${section}`}
                      >
                        {section}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {uscSections.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                    <Scale className="w-3 h-3" />
                    USC Sections:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {uscSections.map((section, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs bg-white cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        onClick={() => onDocumentClick?.({ id: section, type: 'Usc', title: `35 USC ${section}`, resource_id: section })}
                        title={`Click to view 35 USC ${section}`}
                      >
                        {section}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {vdbResources.classification_codes && vdbResources.classification_codes.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2">Classification Codes:</div>
                  <div className="flex flex-wrap gap-1">
                    {vdbResources.classification_codes.map((code, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-white">
                        {code}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {searchTerms.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2">Search Terms:</div>
                  <div className="flex flex-wrap gap-1">
                    {searchTerms.map((term, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {term}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Expand/Collapse Button */}
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 flex-shrink-0 self-start"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <ChevronUp className="w-8 h-8 text-blue-600" />
          ) : (
            <ChevronDown className="w-8 h-8 text-blue-600" />
          )}
        </Button>
      </div>
    </div>
  );
};

export const MessageContext = memo(MessageContextComponent, (prevProps, nextProps) => {
  return (
    prevProps.messageContext === nextProps.messageContext &&
    prevProps.sessionContext === nextProps.sessionContext &&
    prevProps.onDocumentClick === nextProps.onDocumentClick &&
    prevProps.compactMode === nextProps.compactMode
  );
});
